import React, { useState } from "react";
import { Typography, Box } from "@mui/material";

import SalaryInfoPanel from "./SalaryInfoPanel";
import List from "../../../components/List";
import { EmptyView } from "../../../components/OList";
import { TSalary } from "../../../interfaces/SalaryInterface";
import { Employee } from "../../../interfaces/User";
import { useSalaries } from "../../../hooks/query/useSalaries";
import { SalaryCreateEdit } from "./SalaryCreateEdit";
import { rupeesDisplayFull } from "../../../helpers/util";
import { ServiceProvider } from "../../../interfaces/ServiceProvidersInterface";
import { convertADtoBS } from "../../../components/Calendar/functions/calendarFunctions";
import { SalaryListHeader } from "./Components/SalaryListHeader";
import { tl } from "../../../components/translate";

interface PropTypes {
  showEmployeeFilter?: boolean;
  employeeIds?: number[];
  serviceProviderIds?: number[];
  enableCreateNew?: boolean;
}

export default function SalaryList({
  showEmployeeFilter = true,
  employeeIds = [],
  serviceProviderIds = [],
  enableCreateNew = true
}: PropTypes): React.JSX.Element {
  const [showCreateEditPanel, setShowCreateEditPanel] = useState(false);
  const [selectedSalary, setSelectedSalary] = useState<TSalary | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | ServiceProvider | null>(null);

  // Salary Month and Year (Nepali BS) and Status states
  const currentBs = convertADtoBS(new Date());

  const [filter, setFilter] = useState<{
    employeeIds: number[];
    serviceProviderIds: number[];
    payrollYear: number;
    payrollMonth: number;
  }>({
    employeeIds: employeeIds || [],
    serviceProviderIds: serviceProviderIds || [],
    payrollYear: currentBs.bsYear,
    payrollMonth: currentBs.bsMonth
  });

  const { data: salaryList = [], isLoading } = useSalaries(filter);

  return (
    <Box
      sx={{
        height: "100%"
      }}
    >
      <List<TSalary>
        automation="salaryList"
        data={salaryList}
        isLoading={isLoading}
        withoutSearch
        isResponsive
        hideCreateButton
        adjustHeightToContents
        headerStyles={{ px: 0, py: 0 }}
        additionalHeaderContent={
          <SalaryListHeader
            enableCreateNew={enableCreateNew}
            showEmployeeFilter={showEmployeeFilter}
            onRunPayroll={() => {
              setShowCreateEditPanel(true);
              setSelectedSalary(null);
            }}
            onRunPyrollForAll={() => {
              setShowCreateEditPanel(true);
              setSelectedSalary(null);
            }}
            selectedEmployee={selectedEmployee}
            setSelectedEmployee={setSelectedEmployee}
            filter={filter}
            setFilter={setFilter}
          />
        }
        activeRow={selectedSalary && selectedSalary.id}
        onRowClick={(ctc) => {
          const unselect = selectedSalary && selectedSalary.id === ctc.id;

          if (unselect) {
            setSelectedSalary(null);
          } else {
            setSelectedSalary(ctc as TSalary);
          }
        }}
        columns={[
          {
            key: "id",
            label: "ID",
            sortable: true,
            formatter: ({ id }) => (
              <Typography component="span" variant="body2">
                {id}
              </Typography>
            )
          },
          {
            key: "name",
            label: "Name",
            sortable: true,
            formatter: (row) => {
              let name = "-";
              if (row.employee) {
                name = `${row.employee.firstName} ${row.employee.lastName}`;
              } else if (row.serviceProvider) {
                name = `${row.serviceProvider.firstName} ${row.serviceProvider.lastName}`;
              }
              return (
                <Typography component="span" variant="body2">
                  {name}
                </Typography>
              );
            }
          },
          {
            key: "type",
            label: "Employee Type",
            sortable: true,
            formatter: (row) => (
              <Typography component="span" variant="body2">
                {row.employeeId ? "Employee" : "Service Provider"}
              </Typography>
            )
          },
          {
            key: "payrollYearMonth",
            label: "Salary Year/Month",
            sortable: true,
            formatter: ({ payrollYear, payrollMonth }) => (
              <Typography component="span" variant="body2">
                {payrollYear}/{payrollMonth}
              </Typography>
            )
          },
          {
            key: "grossMonthlyIncome",
            label: "Gross Monthly Income",
            sortable: true,
            formatter: ({ salarySummary }) => (
              <Typography component="span" variant="body2">
                {rupeesDisplayFull(salarySummary?.salary?.grossMonthlySalary)}
              </Typography>
            )
          },
          {
            key: "netTaxLiabilityMonthly",
            label: "Net Tax Liability Monthly",
            sortable: true,
            formatter: ({ salarySummary }) => (
              <Typography component="span" variant="body2">
                {rupeesDisplayFull(salarySummary?.tax?.netCurrentMonthTax)}
              </Typography>
            )
          },
          {
            key: "taxAdjustment",
            label: tl("salary.taxAdjustment"),
            sortable: true,
            formatter: ({ salarySummary }) => (
              <Typography component="span" variant="body2">
                {rupeesDisplayFull(salarySummary?.tax?.taxAdjustment || 0)}
              </Typography>
            )
          },
          {
            key: "netMonthlySalary",
            label: "Net Monthly Salary",
            sortable: true,
            formatter: ({ salarySummary }) => (
              <Typography component="span" variant="body2">
                {rupeesDisplayFull(salarySummary?.salary?.netMonthlySalary)}
              </Typography>
            )
          },
          {
            key: "totalPayable",
            label: "Total Payable",
            sortable: true,
            formatter: ({ salarySummary }) => (
              <Typography component="span" variant="body2">
                {rupeesDisplayFull(salarySummary?.salary?.totalPayable)}
              </Typography>
            )
          }
        ]}
      >
        <EmptyView>
          <Typography component="div" variant="body2">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "350px",
                width: "100%"
              }}
            >
              <Typography component="div">
                <Box textAlign="center" marginTop="16px">
                  No salary records found
                </Box>
              </Typography>
            </Box>
          </Typography>
        </EmptyView>
      </List>

      {selectedSalary && (
        <SalaryInfoPanel
          salaryData={selectedSalary}
          onClose={() => setSelectedSalary(null)}
          onEditOpen={() => setShowCreateEditPanel(true)}
          onDeleteSucces={() => {
            setSelectedSalary(null);
          }}
        />
      )}

      {showCreateEditPanel && (
        <SalaryCreateEdit
          onClose={() => {
            setShowCreateEditPanel(false);
            setSelectedSalary(null);
          }}
          salary={selectedSalary}
          onCreateSuccess={() => {
            setSelectedSalary(null);
            setShowCreateEditPanel(false);
          }}
          onUpdateSuccess={() => {
            setSelectedSalary(null);
            setShowCreateEditPanel(false);
          }}
        />
      )}
    </Box>
  );
}
